// libraryQuoteInsert — pure helper for #33 "insert library item as quote".
//
// Builds the TipTap nodes/HTML the BriefEditor needs to drop a library item
// in as a quote-shell with an attribution line. Optionally wraps the title
// in an Amazon Associates affiliate link when (a) the workspace has a valid
// Associates tag, (b) the source is a book/chapter, and (c) the item has a
// usable ISBN-10 (or 978-prefix ISBN-13 we can convert).
//
// The eligibility decision is exposed separately so the caller can prompt
// the user with an opt-in checkbox before insertion.

import {
  AFFILIATE_LINK_CLASSNAME,
  AFFILIATE_TOOLTIP,
  buildAmazonAffiliateUrl,
  isValidAssociatesTag,
} from "@/lib/amazonAffiliate";

export type LibraryQuoteSource = {
  title: string | null;
  author: string | null;
  source_type: string | null;
  isbn_10: string | null;
  isbn_13: string | null;
  published_year: number | null;
};

export type BuildLibraryQuoteInsertParams = {
  source: LibraryQuoteSource;
  workspaceAssociatesTag: string | null;
  includeAffiliateLink: boolean;
};

export type LibraryQuoteInsert = {
  /** TipTap node spec passed to `editor.insertContentAt(pos, ...)`. */
  blockquoteNode: {
    type: "blockquote";
    content: Array<{ type: "paragraph"; content?: unknown[] }>;
  };
  /** HTML for the muted attribution paragraph that follows the blockquote. */
  attributionHtml: string;
  /** True when the workspace + item combo could produce an affiliate URL. */
  amazonEligible: boolean;
  /** Resolved affiliate URL when eligible + included; null otherwise. */
  amazonUrl: string | null;
};

const escapeHtml = (raw: string): string =>
  raw.replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const isLinkableSource = (sourceType: string | null): boolean =>
  sourceType === "book" || sourceType === "chapter";

/**
 * Decide whether the (workspace, item) pair could yield an affiliate link.
 * Returns the resolved URL when yes (regardless of the user's opt-in choice).
 */
export const resolveAmazonEligibility = (
  source: LibraryQuoteSource,
  workspaceAssociatesTag: string | null,
): { eligible: boolean; url: string | null } => {
  if (!isValidAssociatesTag(workspaceAssociatesTag)) return { eligible: false, url: null };
  if (!isLinkableSource(source.source_type)) return { eligible: false, url: null };
  const isbn = source.isbn_10 ?? source.isbn_13 ?? null;
  if (!isbn) return { eligible: false, url: null };
  const url = buildAmazonAffiliateUrl({ isbn, associatesTag: workspaceAssociatesTag });
  return { eligible: !!url, url };
};

export const buildLibraryQuoteInsert = ({
  source,
  workspaceAssociatesTag,
  includeAffiliateLink,
}: BuildLibraryQuoteInsertParams): LibraryQuoteInsert => {
  const blockquoteNode = {
    type: "blockquote" as const,
    content: [{ type: "paragraph" as const }],
  };

  const { eligible, url } = resolveAmazonEligibility(source, workspaceAssociatesTag);
  const useLink = eligible && includeAffiliateLink && !!url;

  const title = (source.title ?? "").trim();
  const author = (source.author ?? "").trim();
  // Note: year is intentionally omitted from the in-draft inline attribution.
  // The year still appears in Citations Preview / footnotes / endnotes /
  // bibliography via the CSL-JSON renderer (`renderCitation`).

  const safeTitle = escapeHtml(title || "Untitled");
  const titleHtml = useLink
    ? `<a href="${escapeHtml(url!)}" target="_blank" rel="noopener noreferrer sponsored" class="${AFFILIATE_LINK_CLASSNAME}" title="${escapeHtml(AFFILIATE_TOOLTIP)}" aria-label="${escapeHtml(AFFILIATE_TOOLTIP)}">${safeTitle}</a>`
    : safeTitle;

  const cite = author ? `${titleHtml}, ${escapeHtml(author)}` : titleHtml;

  const attributionHtml =
    `<p class="text-sm text-muted-foreground not-italic"><em>— ${cite}</em></p>`;

  return {
    blockquoteNode,
    attributionHtml,
    amazonEligible: eligible,
    amazonUrl: useLink ? url : null,
  };
};
